// Render backend selection (see docs/render-backend-interface.md).
//
// A canvas is either a WebGL2 context OR a WebGPU device per frame — never both — so the
// backend is chosen once, up front. WebGPU is preferred when available (it enables the
// GPU-driven world path); WebGL2 is the universal fallback. This module only decides and
// constructs; it holds no render state.

import * as com from '../com'

export type BackendKind = 'webgpu' | 'webgl2'

// True only when the runtime exposes WebGPU AND an adapter is actually obtainable. Presence of
// `navigator.gpu` is not enough — some environments expose the object but fail requestAdapter.
export const webgpuAvailable = async (): Promise<boolean> => {
  try {
    const gpu = navigator.gpu
    if (gpu == null || typeof gpu.requestAdapter !== 'function') return false
    const adapter = await gpu.requestAdapter()
    return adapter != null
  } catch (_e) {
    return false
  }
}

// The WebGL2 safety hatch: `-webgl` or `-nowebgpu` (aliases) force the fallback backend even
// where WebGPU works. `-webgpu` is accepted as an explicit opt-in but is redundant now that
// WebGPU is the default. vid.init is the only caller — it owns the actual construction.
// checkParm returns the arg INDEX (0 = absent), so coerce rather than returning it.
export const forceWebGL = (): boolean => !!com.checkParm('-webgl') || !!com.checkParm('-nowebgpu')
